import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, RefreshCw, ChevronDown } from "lucide-react";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";

/* ─── Category filter logic ────────────────────────────────── */
const TOPS = ["NIGHTCALL Tee", "Racer Jersey", "Racer Longsleeve", "LIMITED EDITION JERSEY HERO STYLE"];
const BOTTOMS = ["Racer Track Pants", "Velocity Mesh Shorts"];
const JEWELRY = ["VVS1 Star Bracelet", "VVS1 Tennis Bracelet", "Tear Bracelet", "Angel Eyes Bracelet"];
const ACCESSORIES = ["MYSTERY BOX", "After Hours Bundle"];

// Prefix match: Shopify titles carry suffixes, e.g. "Racer Track Pants (REFLECTIVE + WATERPROOF)".
const matches = (list, name) => list.some((n) => name.startsWith(n.toLowerCase()));

function categorize(product) {
  const n = (product.name || "").toLowerCase();
  if (matches(TOPS, n)) return "tops";
  if (matches(BOTTOMS, n)) return "bottoms";
  if (matches(JEWELRY, n)) return "jewelry";
  if (matches(ACCESSORIES, n)) return "accessories";
  return "tops";
}

const TABS = [
  { key: "all", label: "View All" },
  { key: "tops", label: "Tops" },
  { key: "bottoms", label: "Bottoms" },
  { key: "jewelry", label: "Jewelry" },
  { key: "accessories", label: "Accessories" },
];

const SORT_OPTIONS = [
  { key: "featured", label: "Featured" },
  { key: "price-asc", label: "Price: Low to High" },
  { key: "price-desc", label: "Price: High to Low" },
  { key: "name-asc", label: "A to Z" },
  { key: "name-desc", label: "Z to A" },
];

export default function ProductListPage() {
  const location = useLocation();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [sortKey, setSortKey] = useState("featured");
  const [sortOpen, setSortOpen] = useState(false);
  const [sizeFilters, setSizeFilters] = useState([]);
  const [priceFilter, setPriceFilter] = useState(null);

  const toggleSize = (s) => setSizeFilters((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  const togglePrice = (p) => setPriceFilter((prev) => prev === p ? null : p);
  const clearFilters = () => { setSizeFilters([]); setPriceFilter(null); };

  const fetchProducts = useCallback(() => {
    setLoading(true); setError(false);
    api.get("/shopify/products").then(({ data }) => {
      if (!data || data.length === 0) throw new Error("empty");
      setAllProducts(data); setError(false);
    }).catch(() => { setAllProducts([]); setError(true); }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts, location]);

  let filtered = activeTab === "all" ? allProducts : allProducts.filter((p) => categorize(p) === activeTab);
  if (sizeFilters.length) filtered = filtered.filter((p) => (p.sizes || []).some((s) => sizeFilters.includes(s)));
  if (priceFilter) filtered = filtered.filter((p) => {
    const pr = p.price || 0;
    if (priceFilter === "under100") return pr < 100;
    if (priceFilter === "100-200") return pr >= 100 && pr <= 200;
    if (priceFilter === "200plus") return pr > 200;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === "price-asc") return (a.price || 0) - (b.price || 0);
    if (sortKey === "price-desc") return (b.price || 0) - (a.price || 0);
    if (sortKey === "name-asc") return (a.name || "").localeCompare(b.name || "");
    if (sortKey === "name-desc") return (b.name || "").localeCompare(a.name || "");
    return 0;
  });

  const sortLabel = SORT_OPTIONS.find((o) => o.key === sortKey)?.label || "Featured";

  const chip = (active) =>
    `flex-shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] border transition-colors whitespace-nowrap ${
      active ? "bg-[#F2F0EB] text-[#0A0A0A] border-[#F2F0EB] font-semibold" : "text-[#8A8A8A] border-[#8A8A8A]/30 hover:border-[#F2F0EB] hover:text-[#F2F0EB]"
    }`;

  return (
    <div data-testid="product-list-page" className="min-h-screen bg-[#0A0A0A]">
      <SEO title="Shop | AERWEAR" description="Shop AERWEAR tops, bottoms and jewelry. Free US shipping." />

      {/* Tabs + Sort */}
      <div className="border-b border-[#8A8A8A]/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between px-4 md:px-8 lg:px-12">
          <div className="flex items-center overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            {TABS.map((tab) => (
              <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
                className="flex-shrink-0 px-4 md:px-6 py-4 font-['Oswald'] text-[11px] uppercase tracking-[0.2em] transition-colors relative"
                style={{ color: activeTab === tab.key ? "#F2F0EB" : "#8A8A8A" }}>
                {tab.label}
                {activeTab === tab.key && <span className="absolute bottom-0 left-4 right-4 h-px bg-[#F2F0EB]" />}
              </button>
            ))}
          </div>

          <div className="relative flex-shrink-0 self-end md:self-auto border-t md:border-t-0 border-[#8A8A8A]/20 w-full md:w-auto flex justify-end">
            <button data-testid="sort-toggle" onClick={() => setSortOpen(!sortOpen)} className="flex items-center gap-1.5 px-4 py-3.5 md:py-4 text-[10px] uppercase tracking-[0.15em] text-[#8A8A8A]">
              Sort by: <span className="text-[#F2F0EB]">{sortLabel}</span> <ChevronDown size={12} />
            </button>
            {sortOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setSortOpen(false)} />
                <div className="absolute right-0 top-full z-50 bg-[#141414] border border-[#8A8A8A]/20 py-1 min-w-[180px]">
                  {SORT_OPTIONS.map((opt) => (
                    <button key={opt.key} data-testid={`sort-${opt.key}`} onClick={() => { setSortKey(opt.key); setSortOpen(false); }}
                      className="block w-full text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] hover:bg-[#F2F0EB]/5 transition-colors"
                      style={{ color: sortKey === opt.key ? "#F2F0EB" : "#8A8A8A" }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filter Chips */}
      <div data-testid="filter-chips" className="px-4 md:px-8 lg:px-12 pt-5 pb-1 flex items-center gap-2 overflow-x-auto scrollbar-hide">
        <span className="flex-shrink-0 text-[9px] uppercase tracking-[0.2em] text-[#8A8A8A] mr-1">Size</span>
        {["S", "M", "L", "XL"].map((s) => (
          <button key={s} data-testid={`chip-size-${s}`} onClick={() => toggleSize(s)} className={chip(sizeFilters.includes(s))}>{s}</button>
        ))}
        <span className="flex-shrink-0 w-px h-4 bg-[#8A8A8A]/30 mx-1" />
        {[{ k: "under100", l: "Under $100" }, { k: "100-200", l: "$100–$200" }, { k: "200plus", l: "$200+" }].map((p) => (
          <button key={p.k} data-testid={`chip-price-${p.k}`} onClick={() => togglePrice(p.k)} className={chip(priceFilter === p.k)}>{p.l}</button>
        ))}
        {(sizeFilters.length > 0 || priceFilter) && (
          <button data-testid="chip-clear" onClick={clearFilters} className="flex-shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] hover:text-[#F2F0EB] underline whitespace-nowrap">Clear</button>
        )}
      </div>

      {/* Count */}
      <div className="px-4 md:px-8 lg:px-12 py-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">{sorted.length} product{sorted.length !== 1 ? "s" : ""}</p>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 size={24} className="animate-spin text-[#8A8A8A]" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-sm uppercase tracking-[0.2em] text-[#8A8A8A]">No products found</p>
          {error && (
            <button data-testid="retry-products-btn" onClick={fetchProducts} className="mt-6 inline-flex items-center gap-2 border border-[#8A8A8A]/30 px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-[#F2F0EB] hover:border-[#F2F0EB] transition-colors">
              <RefreshCw size={14} /> Retry
            </button>
          )}
        </div>
      ) : (
        <div className="px-4 md:px-8 lg:px-12 pb-20 grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6 sm:gap-y-12">
          {sorted.map((product) => <ProductCard key={product.id} product={product} />)}
        </div>
      )}
    </div>
  );
}
